/**
 * CreditsView - Abo & Guthaben (Pricing v4)
 *
 * Features:
 * - Bild-Abo: 3 Pläne mit Monats-/Jahres-Toggle (jährlich = 2 Monate gratis)
 * - Grundriss-Paket als Opt-in-Checkbox je Plan (Preis passt sich live an)
 * - Bild-Credit-Top-up-Slider (1,50 €/Credit, nur mit aktivem Abo)
 * - Einzel-Grundriss-Kauf (15 €, kein Abo nötig)
 * - Kosten-Legende, FAQ, Kauf-/Nutzungshistorie
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Coins,
  CreditCard,
  Check,
  History,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  HelpCircle,
  LayoutTemplate,
  RefreshCw,
  ExternalLink,
  Ruler,
  TrendingUp,
  ImageIcon,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import {
  SUBSCRIPTION_PLANS,
  SubscriptionPlan,
  PlanId,
  BillingCycle,
  TOPUP_CONFIG,
  FLOORPLAN_SINGLE_CENTS,
  planMonthlyCents,
  addonMonthlyCents,
  getPlanById,
  perImageLabel,
  formatPrice,
  formatEuro,
} from '@/lib/stripeConfig';
import { track } from '@/lib/analytics';
import { formatCredits } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { latestStagingPrices } from '@/lib/stagingPriceDisplay';
import {
  startSubscriptionCheckout,
  buyTopupCredits,
  buySingleFloorplan,
  setFloorplanAddon,
  changePlan,
  cancelScheduledChange,
  fetchSubscriptionInfo,
  createCustomerPortalLink,
  fetchTransactions,
  fetchCreditUsage,
  Transaction,
  CreditUsage,
  CreditState,
  ScheduledChange,
} from '@/services/paymentService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

// ============================================================================
// Constants
// ============================================================================

const FAQ_ITEMS = [
  {
    question: 'Verfallen meine Credits?',
    answer:
      'Die monatlichen Abo-Credits erneuern sich zum Monatsbeginn deines Abos – nicht genutzte Monats-Credits verfallen. Zugekaufte Top-up-Credits und Einzel-Grundrisse verfallen dagegen nie.',
  },
  {
    question: 'Was passiert, wenn meine Monats-Credits aufgebraucht sind?',
    answer:
      'Du kannst jederzeit Top-up-Credits über den Slider dazukaufen (1,50 € pro Credit). Sie verfallen nie und werden erst genutzt, wenn deine Monats-Credits aufgebraucht sind.',
  },
  {
    question: 'Was kostet Nachbessern?',
    answer:
      'Bild-Nachbessern kostet 0,5 Credits – den halben Preis einer neuen Generierung. Grundriss-Nachbessern ist gratis. Wir liefern, bis das Ergebnis sitzt.',
  },
  {
    question: 'Kann ich mein Abo wechseln oder kündigen?',
    answer:
      'Ja, monatlich kündbar. Plan-Wechsel und Kündigung laufen über das Stripe-Kundenportal (Button unten). Das Grundriss-Paket kannst du direkt hier per Klick dazubuchen oder entfernen.',
  },
  {
    question: 'Kann ich eine Rechnung erhalten?',
    answer:
      'Ja, nach jedem Kauf erhältst du automatisch eine Rechnung per E-Mail. Du kannst alle Rechnungen auch jederzeit über dein Stripe-Kundenportal einsehen und herunterladen.',
  },
  {
    question: 'Darf ich die Bilder kommerziell nutzen?',
    answer:
      'Ja, du darfst alle generierten Bilder uneingeschränkt kommerziell nutzen – in Exposés, auf Immobilienportalen, Social Media und deiner Website.',
  },
];

const USAGE_ACTION_LABELS: Record<string, string> = {
  generation: 'Bild-Generierung',
  refinement: 'Nachbessern',
  refinement_free: 'Nachbessern (kostenlos)',
  upscale: 'Upscaling',
};

const usageActionLabel = (action: string): string => {
  if (USAGE_ACTION_LABELS[action]) return USAGE_ACTION_LABELS[action];
  if (action.startsWith('floorplan_unit')) return 'Grundriss (2D + 3D)';
  if (action.startsWith('floorplan_free')) return 'Grundriss (kostenlos)';
  if (action.startsWith('floorplan_refinement')) return 'Grundriss-Nachbessern (kostenlos)';
  if (action.startsWith('floorplan')) return 'Grundriss';
  return action;
};

// ============================================================================
// Component
// ============================================================================

interface CreditsViewProps {
  credits: number;
  creditState: CreditState | null;
  onCreditStateRefresh?: () => void;
}

export const CreditsView: React.FC<CreditsViewProps> = ({
  credits,
  creditState,
  onCreditStateRefresh,
}) => {
  const { user } = useAuth();
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [addonChecked, setAddonChecked] = useState<Record<string, boolean>>({});
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [usageHistory, setUsageHistory] = useState<CreditUsage[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [topupCredits, setTopupCredits] = useState<number>(TOPUP_CONFIG.defaultValue);
  const [singleFloorplanCount, setSingleFloorplanCount] = useState<number>(1);
  const [stagingPrices, setStagingPrices] = useState({ stagedFirst: 1, emptyFirst: 1, refinement: 0 });
  const [scheduledChange, setScheduledChange] = useState<ScheduledChange | null>(null);

  const hasActiveSubscription = creditState?.subscriptionStatus === 'active';
  const currentPlanId = hasActiveSubscription ? creditState?.plan ?? null : null;

  // Vorgemerkten Plan-Wechsel (Downgrade) live aus Stripe laden.
  const refreshScheduledChange = async () => {
    if (creditState?.subscriptionStatus !== 'active') { setScheduledChange(null); return; }
    const { info } = await fetchSubscriptionInfo();
    setScheduledChange(info?.scheduledChange ?? null);
  };

  const loadHistory = async () => {
    if (!user) return;
    setLoadingHistory(true);
    try {
      const [txs, usage] = await Promise.all([
        fetchTransactions(user.id),
        fetchCreditUsage(user.id, 20),
      ]);
      setTransactions(txs);
      setUsageHistory(usage);
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Check for success/cancel query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setSuccessMessage('Zahlung erfolgreich! Dein Guthaben wurde aktualisiert.');
      track('Purchase', { currency: 'EUR' });
      window.history.replaceState({}, '', window.location.pathname);
      // Reload after a short delay to ensure the webhook has finished processing
      const timer = setTimeout(() => {
        loadHistory();
        onCreditStateRefresh?.();
      }, 4000);
      return () => clearTimeout(timer);
    }
    if (params.get('canceled') === 'true') {
      setError('Zahlung abgebrochen. Du kannst es jederzeit erneut versuchen.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Load history on mount and when credits change (e.g. after Realtime update)
  useEffect(() => {
    loadHistory();
  }, [user, credits]);

  // Load live staging prices for the "Was kostet was" table
  useEffect(() => {
    supabase
      .from('credit_pricing')
      .select('version_type, event_type, credit_cost, effective_from')
      .eq('tool', 'staging')
      .then(({ data }) => { if (data) setStagingPrices(latestStagingPrices(data)); });
  }, []);

  // Vorbelegung des Abo-Zyklus + Add-on-Checkbox aus dem aktiven Abo.
  // Alle Tarife auf den aktuellen Add-on-Zustand vorbelegen, damit ein Wechsel
  // das Grundriss-Paket standardmäßig mitnimmt (je Karte abwählbar).
  useEffect(() => {
    if (hasActiveSubscription && creditState) {
      if (creditState.billingCycle) setCycle(creditState.billingCycle);
      setAddonChecked((prev) => {
        const next = { ...prev };
        for (const p of SUBSCRIPTION_PLANS) {
          if (!(p.id in prev)) next[p.id] = creditState.grundrissAddon;
        }
        return next;
      });
    }
  }, [hasActiveSubscription, creditState?.plan, creditState?.grundrissAddon, creditState?.billingCycle]);

  // Vorgemerkten Plan-Wechsel (Downgrade) live aus Stripe laden.
  useEffect(() => {
    if (creditState?.subscriptionStatus !== 'active') { setScheduledChange(null); return; }
    let active = true;
    fetchSubscriptionInfo().then(({ info }) => { if (active) setScheduledChange(info?.scheduledChange ?? null); });
    return () => { active = false; };
  }, [creditState?.subscriptionStatus, creditState?.plan, creditState?.grundrissAddon, creditState?.billingCycle]);

  const topupPriceCents = topupCredits * TOPUP_CONFIG.unitCents;
  const singleFloorplanPriceCents = singleFloorplanCount * FLOORPLAN_SINGLE_CENTS;

  // ==========================================================================
  // Handlers
  // ==========================================================================

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    setError(null);
    setPendingAction(`subscribe-${plan.id}`);
    const withAddon = !!addonChecked[plan.id];
    track('Checkout Started', {
      type: 'subscription',
      plan: plan.id,
      billing_cycle: cycle,
      with_floorplan_addon: withAddon,
    });

    const result = await startSubscriptionCheckout(plan.id, cycle, withAddon);
    if (!result.success) {
      setError(result.error || 'Fehler beim Kaufprozess');
      setPendingAction(null);
    }
  };

  const handleChangePlan = async (plan: SubscriptionPlan) => {
    const targetCents = planMonthlyCents(plan, cycle);
    const currentCents = currentPlanId
      ? planMonthlyCents(getPlanById(currentPlanId)!, creditState?.billingCycle ?? cycle)
      : 0;
    const isUpgrade = targetCents >= currentCents;
    const priceLabel = cycle === 'yearly'
      ? `${formatEuro(targetCents)}/Monat · jährlich abgerechnet (${formatEuro(targetCents * 12)}/Jahr)`
      : `${formatEuro(targetCents)}/Monat`;
    const effectiveHint = renewalDate ?? 'zum Ende der aktuellen Abrechnungsperiode';
    // Grundriss-Paket-Wahl dieser Karte mit in den Wechsel geben.
    const withAddon = !!addonChecked[plan.id];
    const hadAddon = !!creditState?.grundrissAddon;
    const addonCentsLabel = formatEuro(addonMonthlyCents(plan, cycle));
    const addonNote = withAddon
      ? (hadAddon
          ? ` Dein Grundriss-Paket wird auf den ${plan.name}-Tarif übernommen (+${addonCentsLabel}/Monat).`
          : ` Das Grundriss-Paket (+${addonCentsLabel}/Monat) wird beim Wechsel dazugebucht.`)
      : (hadAddon ? ' Dein Grundriss-Paket wird beim Wechsel entfernt.' : '');
    const confirmText = isUpgrade
      ? `Auf ${plan.name} (${priceLabel}) upgraden? Der anteilige Betrag wird sofort berechnet und dein neues Kontingent ist sofort verfügbar.${addonNote}`
      : `Zu ${plan.name} (${priceLabel}) wechseln? Der Wechsel wird zum ${effectiveHint} wirksam — bis dahin läuft dein aktuelles Abo voll weiter. Es wird nichts berechnet oder erstattet.${addonNote}`;
    if (!window.confirm(confirmText)) return;

    setError(null);
    setPendingAction(`change-${plan.id}`);
    const result = await changePlan(plan.id, cycle, withAddon);
    setPendingAction(null);

    if (result.success) {
      setSuccessMessage(isUpgrade
        ? `Dein Abo wurde auf ${plan.name} umgestellt.`
        : `Downgrade auf ${plan.name} zum ${renewalDate ?? 'Periodenende'} vorgemerkt. Dein aktuelles Abo läuft bis dahin voll weiter.`);
      track('Plan Changed', { plan: plan.id, billing_cycle: cycle, direction: isUpgrade ? 'upgrade' : 'downgrade' });
      onCreditStateRefresh?.();
      refreshScheduledChange();
    } else {
      setError(result.error || 'Plan-Wechsel fehlgeschlagen');
    }
  };

  const handleCancelScheduledChange = async () => {
    setError(null);
    setPendingAction('cancel-scheduled');
    const result = await cancelScheduledChange();
    setPendingAction(null);
    if (result.success) {
      setSuccessMessage('Vorgemerkter Wechsel abgebrochen. Dein aktuelles Abo läuft normal weiter.');
      setScheduledChange(null);
      track('Scheduled Change Canceled');
      onCreditStateRefresh?.();
    } else {
      setError(result.error || 'Abbrechen fehlgeschlagen');
    }
  };

  const handleAddonToggle = async (plan: SubscriptionPlan, enabled: boolean) => {
    // Ohne aktives Abo: lokale Vorwahl NUR für diese Karte (bundelt in deren Checkout).
    if (!hasActiveSubscription) {
      setAddonChecked((prev) => ({ ...prev, [plan.id]: enabled }));
      return;
    }
    // Anderer als der aktuelle Tarif: nur lokale Vorwahl — wird beim Wechsel angewandt.
    if (plan.id !== currentPlanId) {
      setAddonChecked((prev) => ({ ...prev, [plan.id]: enabled }));
      return;
    }

    const priceLabel = formatEuro(addonMonthlyCents(plan, creditState?.billingCycle ?? 'monthly'));
    const confirmText = enabled
      ? `Grundriss-Paket für ${priceLabel}/Monat dazubuchen? Der anteilige Betrag wird sofort über deine hinterlegte Karte abgebucht (keine Weiterleitung), das Kontingent ist sofort verfügbar.`
      : 'Grundriss-Paket kündigen? Es läuft zum Monatsende aus, deine bereits gutgeschriebenen Grundrisse bleiben erhalten. Es wird nichts zurückerstattet.';
    if (!window.confirm(confirmText)) return;

    setError(null);
    setPendingAction('addon');
    const result = await setFloorplanAddon(enabled);
    setPendingAction(null);

    if (result.success) {
      setAddonChecked((prev) => ({ ...prev, [plan.id]: enabled }));
      setSuccessMessage(enabled ? 'Grundriss-Paket wurde dazugebucht.' : 'Grundriss-Paket läuft zum Monatsende aus.');
      track('Floorplan Addon Toggled', { enabled, plan: plan.id });
      onCreditStateRefresh?.();
    } else {
      setError(result.error || 'Add-on-Änderung fehlgeschlagen');
    }
  };

  const handleTopup = async () => {
    setError(null);
    setPendingAction('topup');
    track('Checkout Started', { type: 'topup', credits: topupCredits, amount_eur: topupPriceCents / 100 });
    const result = await buyTopupCredits(topupCredits);
    if (!result.success) {
      setError(result.error || 'Fehler beim Kaufprozess');
      setPendingAction(null);
    }
  };

  const handleSingleFloorplan = async () => {
    setError(null);
    setPendingAction('floorplan-single');
    track('Checkout Started', { type: 'floorplan_single', quantity: singleFloorplanCount, amount_eur: singleFloorplanPriceCents / 100 });
    const result = await buySingleFloorplan(singleFloorplanCount);
    if (!result.success) {
      setError(result.error || 'Fehler beim Kaufprozess');
      setPendingAction(null);
    }
  };

  const handleOpenPortal = async () => {
    setError(null);
    setPendingAction('portal');
    const { url, error: portalError } = await createCustomerPortalLink();
    setPendingAction(null);
    if (url) {
      window.location.href = url;
    } else {
      setError(portalError || 'Kundenportal konnte nicht geöffnet werden');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30 gap-1">
            <CheckCircle className="w-3 h-3" /> Abgeschlossen
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30 gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Ausstehend
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30 gap-1">
            <XCircle className="w-3 h-3" /> Fehlgeschlagen
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-muted text-muted-foreground border-border gap-1">
            {status}
          </Badge>
        );
    }
  };

  const renewalDate = useMemo(() => {
    if (!creditState?.creditsRenewsAt) return null;
    return new Date(creditState.creditsRenewsAt).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }, [creditState?.creditsRenewsAt]);

  // Tarif-Leiter: Starter → Pro → Agency. Basis für die Upsell-Empfehlung.
  const currentPlan = currentPlanId ? getPlanById(currentPlanId) ?? null : null;
  const recommendedPlan = useMemo<SubscriptionPlan | null>(() => {
    // Ohne Abo: Pro als Einstiegsempfehlung. Mit Abo: der nächsthöhere Tarif
    // (null = Nutzer ist bereits auf dem größten Plan).
    if (!hasActiveSubscription) return getPlanById('pro') ?? null;
    const order: PlanId[] = ['starter', 'pro', 'agency'];
    const idx = currentPlanId ? order.indexOf(currentPlanId as PlanId) : -1;
    if (idx < 0) return null;
    const nextId = order[idx + 1];
    return nextId ? getPlanById(nextId) ?? null : null;
  }, [hasActiveSubscription, currentPlanId]);

  const cycleLabel = cycle === 'yearly' ? 'jährlich' : 'monatlich';

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div className="space-y-10 p-6 max-w-6xl mx-auto animate-fade-in">
      {/* ================================================================ */}
      {/* Status: Abo + Guthaben auf einen Blick                           */}
      {/* ================================================================ */}
      <section className="grid gap-4 lg:grid-cols-[1.1fr_1fr]">
        {/* Abo-Status */}
        <Card className="bg-gradient-to-br from-card to-primary/5 border-border shadow-sm">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <CreditCard className="w-4 h-4" /> Dein Abo
            </div>

            {hasActiveSubscription && currentPlan ? (
              <>
                <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
                  <span className="text-3xl font-bold text-foreground">{currentPlan.name}</span>
                  <Badge variant="outline" className="border-primary/30 text-primary">
                    {cycleLabel}
                  </Badge>
                  {creditState?.grundrissAddon && (
                    <Badge variant="outline" className="border-border text-muted-foreground">
                      inkl. Grundriss-Paket
                    </Badge>
                  )}
                </div>

                <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                  {renewalDate && (
                    <p className="flex items-center gap-1.5">
                      <RefreshCw className="w-3.5 h-3.5 shrink-0" /> Credits erneuern sich am {renewalDate}
                    </p>
                  )}
                  {scheduledChange && (
                    <p className="flex items-center gap-1.5 font-medium text-primary">
                      <TrendingUp className="w-3.5 h-3.5 shrink-0" />
                      Wechsel zu {getPlanById(scheduledChange.plan)?.name ?? scheduledChange.plan} am{' '}
                      {new Date(scheduledChange.effectiveDate).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}{' '}
                      vorgemerkt
                    </p>
                  )}
                </div>

                <div className="mt-auto pt-5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenPortal}
                    disabled={pendingAction === 'portal'}
                  >
                    {pendingAction === 'portal' ? (
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ExternalLink className="mr-2 h-3.5 w-3.5" />
                    )}
                    Abo verwalten
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="mt-3">
                  <span className="text-3xl font-bold text-foreground">Kein aktives Abo</span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Wähle unten einen Plan, um monatliche Bild-Credits, Grundrisse und Top-ups
                  freizuschalten.
                </p>
                <div className="mt-auto pt-5">
                  <Button
                    size="sm"
                    onClick={() => recommendedPlan && handleSubscribe(recommendedPlan)}
                    disabled={pendingAction !== null || !recommendedPlan}
                  >
                    Plan wählen
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Guthaben */}
        <Card className="border-border shadow-sm">
          <CardContent className="p-6 grid grid-cols-2 gap-4 h-full">
            <div className="flex flex-col">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <ImageIcon className="w-4 h-4" /> Bild-Credits
              </div>
              <span className="mt-2 text-4xl font-bold tabular-nums text-foreground">
                {formatCredits(credits)}
              </span>
              {creditState && (
                <p className="mt-auto pt-3 text-xs text-muted-foreground leading-relaxed">
                  {formatCredits(creditState.imageCreditsMonth)} monatlich
                  {creditState.monthlyImageAllowance > 0 && ` / ${creditState.monthlyImageAllowance}`}
                  <br />
                  {formatCredits(creditState.imageCreditsTopup)} Top-up · verfallen nie
                </p>
              )}
            </div>

            <div className="flex flex-col border-l border-border pl-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Ruler className="w-4 h-4" /> Grundrisse
              </div>
              <span className="mt-2 text-4xl font-bold tabular-nums text-foreground">
                {creditState?.totalFloorplanUnits ?? 0}
              </span>
              {creditState && (
                <p className="mt-auto pt-3 text-xs text-muted-foreground leading-relaxed">
                  {creditState.floorplanQuotaMonth} aus Paket
                  {creditState.monthlyFloorplanAllowance > 0 &&
                    ` / ${creditState.monthlyFloorplanAllowance}`}
                  <br />
                  {creditState.floorplanSingles} einzeln · verfallen nie
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* ================================================================ */}
      {/* Upsell / Empfehlung — der klare nächste Schritt                  */}
      {/* ================================================================ */}
      {recommendedPlan ? (
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/10 to-transparent p-5 flex flex-wrap items-center gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <TrendingUp className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">
              {hasActiveSubscription && currentPlan
                ? `Mehr Volumen? Upgrade von ${currentPlan.name} auf ${recommendedPlan.name}`
                : `Empfohlen für den Einstieg: ${recommendedPlan.name}`}
            </p>
            <p className="text-sm text-muted-foreground">
              {hasActiveSubscription && currentPlan
                ? `+${recommendedPlan.imageCredits - currentPlan.imageCredits} Bild-Credits/Monat · ${perImageLabel(recommendedPlan, cycle)}`
                : `${recommendedPlan.imageCredits} Bild-Credits/Monat · ${perImageLabel(recommendedPlan, cycle)}`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-4">
            <div className="text-right">
              <div className="text-lg font-bold tabular-nums text-foreground">
                {formatEuro(planMonthlyCents(recommendedPlan, cycle))}
                <span className="text-xs font-normal text-muted-foreground"> / Monat</span>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() =>
                hasActiveSubscription
                  ? handleChangePlan(recommendedPlan)
                  : handleSubscribe(recommendedPlan)
              }
              disabled={pendingAction !== null}
            >
              {hasActiveSubscription
                ? `Auf ${recommendedPlan.name} upgraden`
                : `${recommendedPlan.name} wählen`}
            </Button>
          </div>
        </div>
      ) : hasActiveSubscription ? (
        <div className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3 text-sm text-muted-foreground">
          <Check className="h-5 w-5 shrink-0 text-emerald-500" />
          <span>
            Du nutzt den größten Plan (<b className="text-foreground">{currentPlan?.name}</b>). Brauchst
            du mehr Bilder? Kauf jederzeit Top-up-Credits dazu.
          </span>
        </div>
      ) : null}

      {/* Success Message */}
      {successMessage && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Erfolgreich</AlertTitle>
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Fehler</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* ================================================================ */}
      {/* Pläne — kompakt, Wechsel & Add-on direkt hier                    */}
      {/* ================================================================ */}
      <div>
        <div className="mb-5 text-center">
          <h2 className="text-lg font-semibold text-foreground">
            {hasActiveSubscription ? 'Plan wechseln' : 'Wähle deinen Plan'}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Alle Tarife monatlich kündbar · Grundriss-Paket optional dazu · ≈ 10 Credits pro Immobilie.
          </p>
        </div>

        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex items-center gap-1 bg-card border border-border rounded-full p-1">
            <button
              type="button"
              onClick={() => setCycle('monthly')}
              className={`text-sm font-semibold px-4 py-1.5 rounded-full transition-colors ${
                cycle === 'monthly' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monatlich
            </button>
            <button
              type="button"
              onClick={() => setCycle('yearly')}
              className={`text-sm font-semibold px-4 py-1.5 rounded-full transition-colors ${
                cycle === 'yearly' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Jährlich <span className="text-emerald-500 text-xs font-bold ml-1">2 Monate gratis</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
          {SUBSCRIPTION_PLANS.map((plan) => {
            const isCurrent = currentPlanId === plan.id;
            const withAddon = !!addonChecked[plan.id];
            const baseCents = planMonthlyCents(plan, cycle);
            const addonCents = addonMonthlyCents(plan, cycle);
            const totalCents = baseCents + (withAddon ? addonCents : 0);
            const isSubscribing = pendingAction === `subscribe-${plan.id}`;

            return (
              <Card
                key={plan.id}
                className={`relative overflow-hidden transition-all duration-200 border-2 flex flex-col ${
                  plan.popular
                    ? 'border-primary shadow-lg shadow-primary/10'
                    : 'border-border hover:border-muted-foreground/50'
                }`}
              >
                {plan.popular && !isCurrent && (
                  <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-bl-lg">
                    EMPFOHLEN
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    AKTUELLER PLAN
                  </div>
                )}

                <CardContent className="p-5 pt-7 flex flex-col flex-1">
                  {/* Name */}
                  <h3 className="text-lg font-bold text-primary">{plan.name}</h3>

                  {/* Price */}
                  <div className="mt-2 mb-3">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-3xl font-bold text-foreground tabular-nums">
                        {formatEuro(totalCents)}
                      </span>
                      <span className="text-muted-foreground text-sm">/ Monat</span>
                    </div>
                    <p className="text-xs text-muted-foreground min-h-[1rem] mt-0.5">
                      {cycle === 'yearly' ? 'pro Monat · jährlich abgerechnet' : ' '}
                    </p>
                    {withAddon && (
                      <p className="text-xs font-semibold text-primary">inkl. Grundriss-Paket</p>
                    )}
                  </div>

                  {/* Credits & Immo estimate */}
                  <div className="mb-4 flex-1">
                    <p className="text-sm font-semibold text-foreground">
                      <span className="text-primary font-bold">{plan.imageCredits}</span> Bild-Credits / Monat
                    </p>
                    <Badge variant="outline" className="mt-2 bg-primary/5 border-primary/20 text-foreground gap-1">
                      🏠 {plan.immoLabel}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-2 tabular-nums">{perImageLabel(plan, cycle)}</p>
                  </div>


                  {/* Grundriss-Paket (optionaler Aufpreis je Plan) */}
                  {(() => {
                    // Immer wählbar: aktueller Tarif togglet live, andere Tarife
                    // merken die Wahl vor und wenden sie beim Plan-Wechsel an.
                    const addonInteractive = true;
                    const addonToggling = pendingAction === 'addon' && isCurrent;
                    return (
                      <label
                        className={`flex items-center gap-3 border rounded-xl px-3 py-2.5 mb-4 transition-colors ${
                          withAddon ? 'border-primary bg-primary/5' : 'border-dashed border-border'
                        } ${addonInteractive ? 'cursor-pointer hover:border-primary/50' : 'opacity-60'} ${addonToggling ? 'pointer-events-none' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={withAddon}
                          onChange={(e) => handleAddonToggle(plan, e.target.checked)}
                          className="w-4 h-4 accent-primary shrink-0"
                          disabled={!addonInteractive || pendingAction !== null}
                        />
                        <span className="flex flex-col leading-tight flex-1">
                          <span className="text-xs font-bold text-foreground">+ Grundriss-Paket</span>
                          <span className="text-[11px] text-muted-foreground">
                            {plan.addon.floorplansPerMonth} Grundriss{plan.addon.floorplansPerMonth !== 1 ? 'e' : ''}/Mo · 2D + 3D · Nachbessern gratis
                          </span>
                        </span>
                        <span className="text-sm font-bold text-primary whitespace-nowrap tabular-nums">
                          {addonToggling ? <Loader2 className="w-4 h-4 animate-spin" /> : `+${formatEuro(addonCents)}`}
                        </span>
                      </label>
                    );
                  })()}

                  {/* CTA */}
                  {isCurrent ? (
                    <Button variant="outline" className="w-full" disabled>
                      <Check className="mr-2 h-4 w-4" />
                      Aktueller Plan
                    </Button>
                  ) : hasActiveSubscription ? (
                    (() => {
                      const targetCents = planMonthlyCents(plan, cycle);
                      const currentCents = currentPlanId
                        ? planMonthlyCents(getPlanById(currentPlanId)!, creditState?.billingCycle ?? cycle)
                        : 0;
                      const isUpgrade = targetCents >= currentCents;
                      const isChanging = pendingAction === `change-${plan.id}`;

                      // Ist dieser Tarif der vorgemerkte Wechsel? Dann Datum + Abbrechen zeigen.
                      if (scheduledChange?.plan === plan.id) {
                        const effDate = new Date(scheduledChange.effectiveDate).toLocaleDateString('de-DE', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                        });
                        return (
                          <div className="space-y-2">
                            <p className="text-xs text-center font-semibold text-primary">
                              Wechsel zum {effDate} vorgemerkt
                            </p>
                            <Button
                              variant="outline"
                              className="w-full"
                              onClick={handleCancelScheduledChange}
                              disabled={pendingAction !== null}
                            >
                              {pendingAction === 'cancel-scheduled' ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Wird abgebrochen...
                                </>
                              ) : (
                                'Vormerkung abbrechen'
                              )}
                            </Button>
                          </div>
                        );
                      }

                      return (
                        <Button
                          onClick={() => handleChangePlan(plan)}
                          disabled={pendingAction !== null}
                          variant={isUpgrade ? 'default' : 'secondary'}
                          className="w-full"
                        >
                          {isChanging ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Wird umgestellt...
                            </>
                          ) : (
                            <>{isUpgrade ? `Auf ${plan.name} upgraden` : `Zu ${plan.name} wechseln`}</>
                          )}
                        </Button>
                      );
                    })()
                  ) : (
                    <Button
                      onClick={() => handleSubscribe(plan)}
                      disabled={pendingAction !== null}
                      variant={plan.popular ? 'default' : 'secondary'}
                      className="w-full"
                    >
                      {isSubscribing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Weiterleitung...
                        </>
                      ) : (
                        <>{plan.name} wählen</>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-4">
          Sichere Zahlung via Stripe · Alle Preise netto zzgl. MwSt. · Monatlich kündbar
        </p>
      </div>

      {/* ================================================================ */}
      {/* Top-up Slider + Zusätzliche Grundrisse                           */}
      {/* ================================================================ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top-up */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Coins className="w-5 h-5 text-amber-500" />
                Zusätzliche Bild-Credits
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Schieb dir dazu, was du brauchst.{' '}
                <span className="font-semibold text-primary">Nur für Bilder · verfallen nie.</span>
              </p>
            </div>

            <div className="flex items-baseline justify-between">
              <p className="text-2xl font-bold tabular-nums">
                <span className="text-primary">{topupCredits}</span> Credits
              </p>
              <p className="text-2xl font-bold tabular-nums">{formatPrice(topupPriceCents)}</p>
            </div>

            <Slider
              value={[topupCredits]}
              onValueChange={(value) => setTopupCredits(value[0])}
              min={TOPUP_CONFIG.min}
              max={TOPUP_CONFIG.max}
              step={TOPUP_CONFIG.step}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground tabular-nums">
              <span>{TOPUP_CONFIG.min}</span>
              <span>{TOPUP_CONFIG.max}</span>
            </div>

            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-primary">{formatPrice(TOPUP_CONFIG.unitCents)}</span> pro Credit ·
              1 Credit = 1 Bild · verfallen nie · nur mit aktivem Abo
            </p>

            <Button
              className="w-full"
              onClick={handleTopup}
              disabled={pendingAction !== null || !hasActiveSubscription}
            >
              {pendingAction === 'topup' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Weiterleitung...
                </>
              ) : (
                'Bild-Credits kaufen'
              )}
            </Button>
            {!hasActiveSubscription && (
              <p className="text-xs text-muted-foreground text-center">
                Top-ups sind nur mit aktivem Abo kaufbar. Wähle zuerst einen Plan.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Zusätzliche Grundrisse */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Ruler className="w-5 h-5 text-primary" />
                Zusätzliche Grundrisse
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Einzelner Grundriss ohne Paket.{' '}
                <span className="font-semibold text-primary">2D + 3D · verfällt nie.</span>
              </p>
            </div>

            <div className="flex items-baseline justify-between">
              <p className="text-2xl font-bold tabular-nums">
                <span className="text-primary">{singleFloorplanCount}</span> Grundriss{singleFloorplanCount !== 1 ? 'e' : ''}
              </p>
              <p className="text-2xl font-bold tabular-nums">{formatPrice(singleFloorplanPriceCents)}</p>
            </div>

            <Slider
              value={[singleFloorplanCount]}
              onValueChange={(value) => setSingleFloorplanCount(value[0])}
              min={1}
              max={15}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground tabular-nums">
              <span>1</span>
              <span>15</span>
            </div>

            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-primary">{formatPrice(FLOORPLAN_SINGLE_CENTS)}</span> pro Grundriss ·
              2D + 3D · verfällt nie · kein Abo nötig
            </p>

            <Button
              className="w-full"
              onClick={handleSingleFloorplan}
              disabled={pendingAction !== null}
            >
              {pendingAction === 'floorplan-single' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Weiterleitung...
                </>
              ) : (
                <>{singleFloorplanCount} Grundriss{singleFloorplanCount !== 1 ? 'e' : ''} kaufen ({formatPrice(singleFloorplanPriceCents)})</>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* ================================================================ */}
      {/* Was kostet was — volle Breite                                    */}
      {/* ================================================================ */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5 text-muted-foreground" />
              Was kostet was
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Bild-Credits gelten nur für Bilder. Grundrisse laufen separat.
            </p>
          </div>

          <div className="divide-y divide-dashed divide-border text-sm">
            <div className="flex items-center justify-between py-2.5">
              <span className="text-foreground">Staging-Bild (eingerichtet)</span>
              <span className="font-mono font-semibold text-primary">{stagingPrices.stagedFirst} Credit{stagingPrices.stagedFirst === 1 ? '' : 's'}</span>
            </div>
            <div className="flex items-center justify-between py-2.5">
              <span className="text-foreground">Leerräumen (Empty)</span>
              <span className="font-mono font-semibold text-primary">{stagingPrices.emptyFirst} Credit{stagingPrices.emptyFirst === 1 ? '' : 's'}</span>
            </div>
            <div className="flex items-center justify-between py-2.5">
              <span className="text-foreground">Nachbessern / Anpassungen</span>
              <span className={`font-mono font-semibold ${stagingPrices.refinement === 0 ? 'text-emerald-500' : 'text-primary'}`}>
                {stagingPrices.refinement === 0 ? 'kostenlos' : `${stagingPrices.refinement} Credit${stagingPrices.refinement === 1 ? '' : 's'}`}
              </span>
            </div>
            <div className="flex items-center justify-between py-2.5 text-muted-foreground">
              <span>2D-/3D-Grundriss</span>
              <span className="font-mono font-semibold">Paket / 15 €</span>
            </div>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 text-xs text-muted-foreground">
            <b className="text-foreground">Monats-Credits</b> erneuern sich am Monatsanfang;{' '}
            <b className="text-foreground">zugekaufte Credits</b> und Einzel-Grundrisse verfallen nie.
          </div>
        </CardContent>
      </Card>

      {/* ================================================================ */}
      {/* FAQ Accordion                                                    */}
      {/* ================================================================ */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-muted-foreground" />
            Häufige Fragen
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {FAQ_ITEMS.map((item, index) => (
              <AccordionItem key={index} value={`faq-${index}`}>
                <AccordionTrigger className="text-foreground hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* ================================================================ */}
      {/* Transaction & Usage History                                      */}
      {/* ================================================================ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Purchase History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-muted-foreground" />
              Kaufhistorie
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : transactions.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-8">
                Noch keine Käufe getätigt
              </p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {transactions.map((tx) => (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between py-2 border-b border-border last:border-0"
                  >
                    <div>
                      <p className="font-medium text-foreground capitalize">{tx.packageName}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(tx.createdAt).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <div className="text-right space-y-1">
                      {tx.amount > 0 && (
                        <p className="font-semibold text-emerald-500">+{tx.amount} Credits</p>
                      )}
                      <div className="flex items-center gap-2 justify-end">
                        <span className="text-xs text-muted-foreground">
                          {formatPrice(tx.priceInCents)}
                        </span>
                        {getStatusBadge(tx.status)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-5 h-5 text-muted-foreground" />
              Nutzungshistorie
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : usageHistory.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-8">
                Noch keine Generierungen
              </p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {usageHistory.map((usage) => (
                  <div
                    key={usage.id}
                    className="flex items-center justify-between py-2 border-b border-border last:border-0"
                  >
                    <div>
                      <p className="font-medium text-foreground">{usageActionLabel(usage.action)}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(usage.createdAt).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <span className={`font-semibold ${usage.creditsUsed > 0 ? 'text-destructive' : 'text-emerald-500'}`}>
                      {usage.creditsUsed > 0 ? `-${usage.creditsUsed}` : 'kostenlos'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
